import React, { useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FaArrowUpRightFromSquare, FaChevronLeft, FaChevronRight, FaCompass } from "react-icons/fa6";
import { useSelector, useDispatch } from "react-redux";
import { getPackages } from "../../slice/khajurahoPackageSlice";
import { getMediaUrl } from "../../common/mediaUrl";

const WeekendTourPreview = () => {
  const scrollRef = useRef(null);
  const dispatch = useDispatch();
  const { data: packages, loading } = useSelector((state) => state.khajurahoPackage);

  // Take only the latest 4 packages
  const displayPackages = packages ? packages.slice(0, 4) : [];

  useEffect(() => {
    dispatch(getPackages());
  }, [dispatch]);

  const scroll = (direction) => {
    const { current } = scrollRef;
    if (current) {
      const scrollAmount = direction === "left" ? -400 : 400;
      current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  return (
    <section className="bg-[#fffbf0] pt-8 pb-14 lg:pt-10 lg:pb-20 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-xl text-center md:text-left">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex items-center justify-center md:justify-start gap-2 mb-3"
            >
              <div className="h-0.5 w-8 bg-orange-main" />
              <span className="text-[10px] font-black uppercase tracking-[0.3em] text-orange-main">Best Tour Packages</span>
            </motion.div>
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl lg:text-4xl font-bold text-gray-dark tracking-tight"
            >
              Explore Our <span className="text-orange-main">Packages</span>
            </motion.h2>
          </div>

          {/* Arrows - Visible only on Mobile (< 768px) */}
          <div className="flex items-center justify-center gap-3 md:hidden">
            <button 
              onClick={() => scroll("left")}
              className="w-12 h-12 rounded-full border border-gray-200 bg-white flex items-center justify-center text-gray-600 hover:bg-orange-main hover:text-white hover:border-orange-main transition-all duration-300 shadow-sm"
            >
              <FaChevronLeft className="text-lg" />
            </button>
            <button 
              onClick={() => scroll("right")}
              className="w-12 h-12 rounded-full border border-gray-200 bg-white flex items-center justify-center text-gray-600 hover:bg-orange-main hover:text-white hover:border-orange-main transition-all duration-300 shadow-sm"
            >
              <FaChevronRight className="text-lg" />
            </button>
          </div>
        </div>

        {/* CONTAINER - Grid on Desktop, Slider on Mobile */}
        <div 
          ref={scrollRef}
          className="flex md:grid md:grid-cols-2 lg:grid-cols-4 gap-6 overflow-x-auto md:overflow-x-visible pb-10 no-scrollbar snap-x snap-mandatory"
        >
          {displayPackages.length > 0 ? displayPackages.map((tour, index) => {
            let firstImage = "";
            try {
              const images = typeof tour.multipleImages === 'string' ? JSON.parse(tour.multipleImages) : tour.multipleImages;
              firstImage = Array.isArray(images) ? images[0] : (typeof images === 'string' ? images.split(',')[0] : "");
            } catch (e) {
              firstImage = tour.multipleImages ? tour.multipleImages.split(',')[0] : "";
            }

            return (
              <motion.div
                key={tour.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="min-w-[280px] md:min-w-0 snap-start h-full"
              >
                <Link to={`/tours/${tour.id}`} className="group block relative aspect-[4/5] rounded-[2.5rem] overflow-hidden bg-gray-100 shadow-xl shadow-black/5 h-full">
                  {/* IMAGE */}
                  <img 
                    src={getMediaUrl(firstImage)} 
                    alt={tour.name} 
                    className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" 
                  />
                  
                  {/* OVERLAY - Stronger at bottom for readability */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-90 group-hover:opacity-100 transition-opacity duration-500" />
  
                  {/* CONTENT */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
                    <div className="flex items-center justify-between gap-4">
                      <span 
                        className="text-[14px] lg:text-[16px] font-black uppercase text-white tracking-[0.1em] leading-snug block drop-shadow-2xl group-hover:text-orange-main transition-colors duration-500 pr-2"
                        style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                      >
                        {tour.name}
                      </span>
                      
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-main flex items-center justify-center text-white shadow-lg transform translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                        <FaArrowUpRightFromSquare className="text-xs" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          }) : !loading && <div className="text-center w-full py-10 text-gray-400">No packages available</div>}
        </div>

        {/* PREMIUM EXPLORE BUTTON */}
        <div className="mt-4 flex justify-center">
           <Link 
            to="/tours" 
            className="group relative px-10 py-4 overflow-hidden rounded-full bg-orange-main text-white font-black text-xs uppercase tracking-widest transition-all duration-500 hover:shadow-[0_15px_30px_rgba(217,116,42,0.3)] active:scale-95 shadow-lg shadow-orange-main/20"
           >
             <span className="relative z-10 flex items-center gap-3">
               Explore More Destinations
               <FaCompass className="text-lg animate-pulse" />
             </span>
             <div className="absolute inset-0 z-0 bg-orange-light translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
           </Link>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}} />
    </section>
  );
};

export default WeekendTourPreview;
